import { handleLoginResponse, requestHeader } from "~/helpers/fetch-helpers";
import {
	cacheEvent,
	cacheSponsorTokens,
	cacheTokens,
	getDeviceId,
	getEventId,
	getSponsorAccessToken,
	removeCachedEvent,
} from "~/helpers/token-helpers";

export const fetchEvent = async( eventId: string | undefined, eventAccessCode: string ) => {	
	try {
		if (!eventId) {
			throw new Error('Event id was not provided');
		}

		const resp = await fetch(`${process.env.REACT_APP_API_BASE_URL}/api/liveEvent/publicEventInfo/${eventId}`);
		const data = await resp.json();

		if (!data?.eventId) { throw new Error('Event not found'); }

		cacheEvent(eventAccessCode, data);

		return {
			ok: true,
			...data
		}
	
	// eslint-disable-next-line @typescript-eslint/no-explicit-any
	} catch (error: any) {
		return {
			ok: false,
			errorMessage: error.errorMessage
		}
	}
}


export const fetchTokens = async ( eventAccessCode: string ) => {
	try {
		const response = await fetch(`${process.env.REACT_APP_API_BASE_URL}/connect/token`, {
			method: 'POST',
			headers: {
				'Content-Type': 'application/x-www-form-urlencoded',
				'Device-Id': getDeviceId(),
			},
			body: new URLSearchParams({
				code: eventAccessCode,
				grant_type: 'otp',
				scope: 'openid offline_access',
				login_hint: 'scanner',
			}),
		});

		if (!response.ok) { return null }
		const tokens = await handleLoginResponse(response);
		if (eventAccessCode) { cacheTokens(eventAccessCode, tokens); }

		return {
			ok: true,
			tokens
		}
	// eslint-disable-next-line @typescript-eslint/no-explicit-any
	} catch (error: any) {
		return {
			ok: false,
			errorMessage: error.message
		}
	}
};

export const getScannerTicket = async (result: string, eventAccessCode: string, currentDate: string, isMultipleScannedInEnabled = false, retryCount = 0) => {
	const requestOptions = {
		method: 'GET',
		headers: { ...requestHeader(eventAccessCode), 'Device-Id': getDeviceId() },
	};

	try {
		const response = await fetch(
			`${process.env.REACT_APP_API_BASE_URL}/api/scanner/ticket/${result}/${currentDate}`,
			requestOptions,
		);

		const resp = await response;
		if ( !resp.ok ) {
			const text = await resp.text();
			const { message } = JSON.parse(text);
			throw new Error(message ?? 'Something was wrong with user ticket');
		} else {
			const data = await response.json();

			if (response.status === 401) {
				refetchWithNewCredentials( eventAccessCode, response.status, null, isMultipleScannedInEnabled, retryCount);
				return;
			}

			if (response.status === 200) {
				const isAdmittedBefore = data.admittedOn && data.admittedOn.some((dt: string) => new Date(dt).toLocaleDateString() === new Date(currentDate).toLocaleDateString());
				return {
					ok: true,
					statusCode: 200,
					participant: { ...data, status: data.error ? 'error' : isAdmittedBefore ? 'duplicate' : 'admitted' },
					status: data.error ? 'error' : isAdmittedBefore ? 'duplicate' : 'admitted',
				}
			}

			if (response.status === 403) {
				refetchWithNewCredentials(eventAccessCode, response.status, data.message, isMultipleScannedInEnabled, retryCount);
				return;
			}

			return {
				ok: false,
				errorMessage: data.message
			}
		}

	// eslint-disable-next-line @typescript-eslint/no-explicit-any
	} catch (e: any) {
		return {
			ok: false,
			errorMessage: e.message
		}
	}

	async function refetchWithNewCredentials(
		eventAccessCode: string,
		responseCode: number,
		responseMessage: string | null,
		isMultipleScannedInEnabled: boolean,
		retryCount: number,
	) {
		if (retryCount > 3) {
			let errorMessage = 'Cannot proceed scanning. Please contact admin with error';
			if (responseCode === 401) {
				errorMessage = `${errorMessage}: ${responseCode}`;
			} else {
				errorMessage = `${errorMessage}: ${responseCode}: ${responseMessage}`;
			}
			
			return {
				ok: false,
				errorMessage: errorMessage
			}
		}
		removeCachedEvent(eventAccessCode);
		await fetchTokens( eventAccessCode );
		await fetchEvent(getEventId(eventAccessCode), eventAccessCode);
		await getScannerTicket(result, eventAccessCode, new Date().toJSON(), isMultipleScannedInEnabled);
	}
};
export const scanOutParticipant = async (
    ticketId: string,
    eventAccessCode: string
) => {
    const requestOptions = {
        method: 'POST',
        headers: {
            ...requestHeader(eventAccessCode),
            'Device-Id': getDeviceId(),
        },
    };

    try {
        const response = await fetch(
            `${process.env.REACT_APP_API_BASE_URL}/api/scanner/ticket/${encodeURIComponent(ticketId)}/scan-out`,
            requestOptions,
        );

        const data = await response.json();

        if (!response.ok) {
            return {
                ok: false,
                errorMessage: data.errorMessage ?? 'Scan out failed',
            };
        }

        return {
            ok: true,
            data,
        };
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    } catch (e: any) {
        return {
            ok: false,
            errorMessage: e.message,
        };
    }
};
export const getParticipantsAdmitted = async( eventId: string, currentDate: string ) => {
	const requestOptions = {
		method: 'GET',
	};

	try {
		const response = await fetch(
			`${process.env.REACT_APP_API_BASE_URL}/api/registration/${eventId}/registrationsAdmitted/${currentDate}`,
			requestOptions,
		);

		const data = await response.json();

		return {
			ok: true,
			participants: data
		}
	// eslint-disable-next-line @typescript-eslint/no-explicit-any
	} catch ( e: any ) {
		return {
			ok: false,
			errorMessage: e.message
		}
	}
}

export const getScannedCount = async( eventAccessCode: string ) => {
	const requestOptions = {
		method: 'GET',
		headers: { ...requestHeader(eventAccessCode), 'Device-Id': getDeviceId() },
	};

	try {
		const response = await fetch(
			`${process.env.REACT_APP_API_BASE_URL}/api/scanner/scanned-count`,
			requestOptions,
		);

		if ( !response.ok ) {
			return {
				ok: false,
				errorMessage: 'Could not load scanned count'
			}
		}

		const data = await response.json();

		return {
			ok: true,
			scannedCount: data.scannedCount
		}
	// eslint-disable-next-line @typescript-eslint/no-explicit-any
	} catch ( e: any ) {
		return {
			ok: false,
			errorMessage: e.message
		}
	}
}

export const consumeVoucherToParticipant = async( eventAccessCode: string, eventId: string, participantId: string ) => {
	const params = JSON.stringify({ eventId: eventId, ticketId: participantId });

	const requestOptions = {
		method: 'POST',
		headers: { ...requestHeader(eventAccessCode), 'Device-Id': getDeviceId() },
		body: params
	};

	try {
		const response = await fetch(
			`${process.env.REACT_APP_API_BASE_URL}/api/scanner/voucher/consume`,
			requestOptions,
		);

		const data = await response.json();

		if ( !response.ok ) {
			return {
				ok: false,
				errorMessage: data.errorMessage
			}
		}

		return {
			ok: true,
			data
		}
	// eslint-disable-next-line @typescript-eslint/no-explicit-any
	} catch ( e: any ) {
		return {
			ok: false,
			errorMessage: e.message
		}
	}
}

export const saveScannerName = async( eventAccessCode: string, eventId: string, scannerName: string ) => {

	const params = JSON.stringify({ eventId: eventId, eventAccessCode: eventAccessCode, scannerName: scannerName });

	const requestOptions = {
		method: 'POST',
		headers: { ...requestHeader(eventAccessCode)},
		body: params
	};

	try {
		const response = await fetch(
			`${process.env.REACT_APP_API_BASE_URL}/api/scanner/addScannerName`,
			requestOptions,
		);

		const data = await response.json();

		if ( !response.ok ) {
			return {
				ok: false,
				errorMessage: data.errorMessage
			}
		}

		return {
			ok: true,
			data
		}
	// eslint-disable-next-line @typescript-eslint/no-explicit-any
	} catch ( e: any ) {
		return {
			ok: false,
			errorMessage: e.message
		}
	}
}
export const scanIn = async (barcodeOrId: string, eventAccessCode: string) => {
    try {
        const response = await fetch(
            `${process.env.REACT_APP_API_BASE_URL}/api/scanner/ticket/${encodeURIComponent(barcodeOrId)}/scan-in`,
            {
                method: 'POST',
                headers: {
                    ...requestHeader(eventAccessCode),
                    'Device-Id': getDeviceId(),
                },
            }
        );
        if (!response.ok) {
            const { errorMessage } = await response.json();
            throw new Error(errorMessage ?? 'Scan-in failed');
        }
        return { ok: true, data: await response.json() };
	// eslint-disable-next-line @typescript-eslint/no-explicit-any
    } catch (e: any) {
        return { ok: false, errorMessage: e.message };
    }
};

export const scanOut = async (barcodeOrId: string, eventAccessCode: string) => {
    try {
        const response = await fetch(
            `${process.env.REACT_APP_API_BASE_URL}/api/scanner/ticket/${encodeURIComponent(barcodeOrId)}/scan-out`,
            {
                method: 'POST',
                headers: {
                    ...requestHeader(eventAccessCode),
                    'Device-Id': getDeviceId(),
                },
            }
        );
        if (!response.ok) {
            const { errorMessage } = await response.json();
            throw new Error(errorMessage ?? 'Scan-out failed');
        }
        return { ok: true, data: await response.json() };
	// eslint-disable-next-line @typescript-eslint/no-explicit-any
    } catch (e: any) {
        return { ok: false, errorMessage: e.message };
    }
};

/* ------------------------------------------------------------------
 * Sponsor lead-capture (booth-side AI matchmaking flow).
 *
 * The scanner app opens `/event/<accessCode>/sponsor-scan/<sponsorId>?t=<token>`
 * from a QR generated by the sponsor portal. We exchange the `t` token for a
 * sponsor-scoped JWT (login_hint = sponsorScanner), then drive the rep picker +
 * camera capture flow. The ticket-scanner and voucher flows are untouched.
 * ------------------------------------------------------------------ */

export const exchangeSponsorScannerToken = async (sponsorId: string, exchangeToken: string) => {
	try {
		const response = await fetch(`${process.env.REACT_APP_API_BASE_URL}/connect/token`, {
			method: 'POST',
			headers: {
				'Content-Type': 'application/x-www-form-urlencoded',
				'Device-Id': getDeviceId(),
			},
			body: new URLSearchParams({
				code: exchangeToken,
				grant_type: 'otp',
				scope: 'openid offline_access',
				login_hint: 'sponsorScanner',
			}),
		});

		if (!response.ok) {
			return { ok: false as const, errorMessage: 'Scanner link expired or invalid' };
		}
		const tokens = await handleLoginResponse(response);
		cacheSponsorTokens(sponsorId, tokens);

		return { ok: true as const, tokens };
	// eslint-disable-next-line @typescript-eslint/no-explicit-any
	} catch (error: any) {
		return { ok: false as const, errorMessage: error.message };
	}
};

function sponsorAuthHeaders(sponsorId: string) {
	const accessToken = getSponsorAccessToken(sponsorId);
	return {
		'Content-Type': 'application/json',
		'Device-Id': getDeviceId(),
		...(accessToken ? { Authorization: `Bearer ${accessToken}` } : {}),
	};
}

export const getSponsorScannerContext = async (sponsorId: string) => {
	try {
		const response = await fetch(
			`${process.env.REACT_APP_API_BASE_URL}/api/scanner/sponsor/context`,
			{ method: 'GET', headers: sponsorAuthHeaders(sponsorId) },
		);
		if (!response.ok) {
			const txt = await response.text();
			throw new Error(safeMessage(txt, 'Could not load sponsor context'));
		}
		return { ok: true as const, data: await response.json() };
	// eslint-disable-next-line @typescript-eslint/no-explicit-any
	} catch (e: any) {
		return { ok: false as const, errorMessage: e.message };
	}
};

export const captureSponsorLead = async (
	sponsorId: string,
	barcode: string,
	representativeId: string,
	note?: string,
) => {
	try {
		const response = await fetch(
			`${process.env.REACT_APP_API_BASE_URL}/api/scanner/sponsor/leads`,
			{
				method: 'POST',
				headers: sponsorAuthHeaders(sponsorId),
				body: JSON.stringify({ barcode, representativeId, note: note ?? null }),
			},
		);
		if (!response.ok) {
			const txt = await response.text();
			throw new Error(safeMessage(txt, 'Could not capture lead'));
		}
		// TODO(offline-queue): when offline mode lands, enqueue + retry here instead of bubbling.
		return { ok: true as const, data: await response.json() };
	// eslint-disable-next-line @typescript-eslint/no-explicit-any
	} catch (e: any) {
		return { ok: false as const, errorMessage: e.message };
	}
};

function safeMessage(text: string, fallback: string): string {
	if (!text) return fallback;
	try {
		const parsed = JSON.parse(text);
		return parsed?.errorMessage || parsed?.message || parsed?.error_description || fallback;
	} catch {
		return fallback;
	}
}